import MoveIcon from "./MoveIcon";
import { TRUST_COLOR, BETRAY_COLOR, MIXED_COLOR, TRUST_BG, BETRAY_BG, MIXED_BG, TEXT_DARK, TEXT_MUTED } from "../theme";

// Shared between the admin's shareable stats card and the player-facing
// daily stats screen, so both look identical.
export function OutcomeDonut({ cc, dd, cd, total, size = 170, centerLabel = "games" }) {
  if (!total) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", background: "#eceef3", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "0.625rem", color: TEXT_MUTED }}>0</div>
      </div>
    );
  }

  const ccPercent = (cc / total) * 100;
  const cdPercent = (cd / total) * 100;
  const inner = Math.round(size * 0.7);

  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `conic-gradient(
        ${TRUST_COLOR} 0% ${ccPercent}%,
        ${MIXED_COLOR} ${ccPercent}% ${ccPercent + cdPercent}%,
        ${BETRAY_COLOR} ${ccPercent + cdPercent}% 100%
      )`,
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{ width: inner, height: inner, borderRadius: "50%", background: "white", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: size >= 150 ? "1.75rem" : "1rem", fontWeight: "700" }}>{total}</div>
        <div style={{ fontSize: size >= 150 ? "0.6875rem" : "0.5625rem", color: TEXT_MUTED }}>{centerLabel}</div>
      </div>
    </div>
  );
}

export function MiniStat({ icon, count, color }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center", gap: "2px", marginBottom: "3px" }}>
        <MoveIcon move={icon[0]} size={11} />
        <MoveIcon move={icon[1]} size={11} flip={icon[1] === "defect"} />
      </div>
      <div style={{ fontSize: "0.8125rem", fontWeight: "700", color }}>{count}</div>
    </div>
  );
}

// When two players picked DIFFERENT answers to the daily question, cc/dd/cd
// alone can't show which answer's picker tended to trust or betray — "one
// betrayed" loses the direction. This lays out the full 2x2: rows are what
// the choices[0]-picker did, columns are what the choices[1]-picker did.
export function Outcome2x2Matrix({ choices, matrix, total }) {
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const cells = [
    { count: matrix.coopCoop, rowMove: "cooperate", colMove: "cooperate", bg: TRUST_BG, color: TRUST_COLOR },
    { count: matrix.coopDefect, rowMove: "cooperate", colMove: "defect", bg: MIXED_BG, color: MIXED_COLOR },
    { count: matrix.defectCoop, rowMove: "defect", colMove: "cooperate", bg: MIXED_BG, color: MIXED_COLOR },
    { count: matrix.defectDefect, rowMove: "defect", colMove: "defect", bg: BETRAY_BG, color: BETRAY_COLOR },
  ];

  return (
    <div style={{ display: "inline-grid", gridTemplateColumns: "auto 1fr 1fr", gap: "4px", alignItems: "center" }}>
      <div />
      <HeaderCell label={choices[1]} move="cooperate" />
      <HeaderCell label={choices[1]} move="defect" />

      <HeaderCell label={choices[0]} move="cooperate" vertical />
      <Cell {...cells[0]} />
      <Cell {...cells[1]} />

      <HeaderCell label={choices[0]} move="defect" vertical />
      <Cell {...cells[2]} />
      <Cell {...cells[3]} />
    </div>
  );

  function Cell({ count, bg, color }) {
    return (
      <div style={{ width: "76px", height: "62px", borderRadius: "8px", background: bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: "1.0625rem", fontWeight: "700", color }}>{count}</div>
        <div style={{ fontSize: "0.625rem", color: TEXT_MUTED }}>{pct(count)}%</div>
      </div>
    );
  }

  function HeaderCell({ label, move, vertical }) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", padding: "2px", maxWidth: vertical ? "54px" : "76px" }}>
        <MoveIcon move={move} size={13} />
        <div style={{ fontSize: "0.5625rem", fontWeight: "600", color: TEXT_DARK, textAlign: "center", lineHeight: "1.1" }}>"{label}"</div>
      </div>
    );
  }
}

export function AgreementBar({ label, rate, count, color }) {
  return (
    <div style={{ textAlign: "center", width: "100px" }}>
      <div style={{ fontSize: "1.125rem", fontWeight: "700", marginBottom: "6px" }}>{rate !== null ? `${rate}%` : "—"}</div>
      <div style={{ height: "70px", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        <div style={{ width: "48px", height: rate !== null ? `${Math.max(4, rate)}%` : "4%", background: color, borderRadius: "8px 8px 0 0" }} />
      </div>
      <div style={{ fontSize: "0.75rem", fontWeight: "600", marginTop: "8px" }}>{label}</div>
      <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>{count} game{count === 1 ? "" : "s"}</div>
    </div>
  );
}
